import React, { memo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Comment } from '../../types';
import type { CommentWithPending } from '../../hooks/useCommentThread';
import { Avatar } from '../common';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { formatRelativeTime } from '../../utils/time';
import { useAuthStore } from '../../store/authStore';

interface CommentItemProps {
  comment: CommentWithPending;
  currentUserId: string;
  // Item-parameterized so the parent can pass stable references and the list
  // doesn't allocate a closure pair per row per render.
  onEdit?: (comment: Comment) => void;
  onDelete?: (commentId: string) => void;
}

function CommentItemComponent({ comment, currentUserId, onEdit, onDelete }: CommentItemProps) {
  const { colors } = useTheme();
  const isAuthor = comment.authorId === currentUserId;
  // Moderators can delete any comment (rules mirror this); editing stays
  // author-only.
  const isModerator = useAuthStore((s) => s.isModerator);
  const isEdited = comment.updatedAt != null;
  const pending = comment.pending === true;

  return (
    <View style={[styles.container, pending && styles.pending]}>
      <Avatar name={comment.authorName} size={28} />
      <View style={styles.content}>
        <Text style={[styles.text, { color: colors.text }]}>
          <Text style={styles.authorName}>{comment.authorName}</Text>
          {'  '}
          {comment.text}
        </Text>
        <View style={styles.meta}>
          <Text
            // textSecondary, not textTertiary: a timestamp is content, and
            // colors.ts scopes textTertiary to "hints/placeholders/disabled
            // only (sub-AA by design)". At 12px on white the tertiary ramp
            // measures 2.72:1 against the 4.5:1 AA floor, which axe caught
            // the first time a comment existed on the audited fixture post.
            style={[styles.timeAgo, { color: colors.textSecondary }]}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualmask: 'true' }}>
            {pending ? 'Sending' : formatRelativeTime(comment.createdAt)}
            {!pending && isEdited ? ' (edited)' : ''}
          </Text>
          {!pending && isAuthor && onEdit && (
            <Pressable onPress={() => onEdit(comment)} hitSlop={8}>
              <Text style={[styles.actionText, { color: colors.textTertiary }]}>Edit</Text>
            </Pressable>
          )}
          {!pending && (isAuthor || isModerator) && onDelete && (
            <Pressable onPress={() => onDelete(comment.id)} hitSlop={8}>
              <Text style={[styles.actionText, { color: colors.textTertiary }]}>Delete</Text>
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingVertical: SPACING.sm,
    alignItems: 'flex-start',
  },
  pending: {
    opacity: 0.6,
  },
  content: {
    flex: 1,
    marginLeft: SPACING.sm,
  },
  authorName: {
    ...TYPOGRAPHY.label,
  },
  text: {
    ...TYPOGRAPHY.bodySmall,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    gap: SPACING.md,
  },
  timeAgo: {
    ...TYPOGRAPHY.bodySmall,
  },
  actionText: {
    ...TYPOGRAPHY.label,
    cursor: 'pointer' as any,
  },
});

// Default shallow compare is correct now that every prop is stable: the
// parent only swaps a comment's object identity when that comment changes,
// and the handlers are item-parameterized useCallbacks.
export const CommentItem = memo(CommentItemComponent);
